import { useContext } from "react";
import { DevUserContext } from "../../contexes/OrderIdContext";
import {
  Badge,
  Box,
  Button,
  Header,
  Pagination,
  Select,
  SpaceBetween,
  Table,
  TextFilter,
} from "@amzn/awsui-components-react";
import { getDlqDateRange, getDlqLogGroup } from "../../helpers/logUtils";
import { getBadgeColour } from "../../helpers/errorUtils";
import EventButtons from "../common/EventButtons";
import DlqRedriveStatusIndicator from "./DlqRedriveStatusIndicator";
import { DLQItem, Stage } from "../../lusca-search-api/generated-src";
import useFetchDlqRedriveInfo from "../../hooks/useFetchDlqRedriveInfo";
import { RedriveLineageFilter } from "../../hooks/useDlqTable";
import { CopyToClipboard } from "../common/CopyToClipboard";

export const RETURNED_AFTER_REDRIVE_LABEL = "Returned after redrive";

const REDRIVE_FILTER_OPTIONS: { value: RedriveLineageFilter; label: string }[] =
  [
    { value: "all", label: "All items" },
    { value: "returned", label: `${RETURNED_AFTER_REDRIVE_LABEL}: yes` },
    { value: "not_returned", label: `${RETURNED_AFTER_REDRIVE_LABEL}: no` },
  ];

/**
 * Latest redrive attempt id for a DLQ row. Set only when this exact message was requeued by the
 * Lusca redriver and then failed again (redrive_attempted); the id is that execution's
 * redriveAttemptId, as shown in the redrive detail view.
 */
function RedriveAttemptCell({ item }: { item: DLQItem }) {
  if (!item.redrive_attempted || !item.redrive_attempt_id) {
    return <Box color="text-status-inactive">—</Box>;
  }
  const attemptId = item.redrive_attempt_id;
  return (
    <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
      <Box fontSize="body-s">
        <code>{attemptId}</code>
      </Box>
      <CopyToClipboard
        variant="inline-icon"
        iconName="copy"
        ariaLabel="Copy redrive attempt id"
        getContent={() => Promise.resolve(attemptId)}
      />
    </SpaceBetween>
  );
}

interface DlqTableProps {
  items: DLQItem[];
  allItems: DLQItem[];
  sortingColumn: any;
  sortingDescending: boolean;
  onSortingChange: (event: any) => void;
  filteringText: string;
  onFilteringChange: (text: string) => void;
  redriveFilter?: RedriveLineageFilter;
  onRedriveFilterChange?: (filter: RedriveLineageFilter) => void;
  currentPageIndex: number;
  onPageChange: (pageIndex: number) => void;
  pageSize: number;
  onItemSelect: (item: DLQItem) => void;
  onErrorSelect: (item: DLQItem) => void;
  filteredItemsCount: number;
  stage: Stage;
  /** Link into DLQ Investigations for the searched transaction (opens in a new tab). */
  investigateHref?: string;
}

export function DlqTable({
  items,
  allItems,
  sortingColumn,
  sortingDescending,
  onSortingChange,
  filteringText,
  onFilteringChange,
  redriveFilter = "all",
  onRedriveFilterChange,
  currentPageIndex,
  onPageChange,
  pageSize,
  onItemSelect,
  onErrorSelect,
  filteredItemsCount,
  stage,
  investigateHref,
}: DlqTableProps) {
  const { redrivenMessages, startRedrive } = useFetchDlqRedriveInfo(stage);
  const devUser = useContext(DevUserContext);

  const handleRequeueAll = () => {
    startRedrive(allItems.map((item) => item.pk));
  };

  const handleRequeueSingle = (messageId: string) => {
    startRedrive([messageId]);
  };

  const columnDefinitions = [
    {
      id: "eventsourcekey",
      header: "Queue",
      cell: (item: DLQItem) => (
        <SpaceBetween direction="horizontal" size="xs">
          <Badge color={getBadgeColour(item.eventsourcekey)}>
            {item.eventsourcekey}
          </Badge>
          {item.is_malformed_json && <Badge color="red">Malformed JSON</Badge>}
          {item.redrive_attempted && (
            <Badge color="blue">{RETURNED_AFTER_REDRIVE_LABEL}</Badge>
          )}
        </SpaceBetween>
      ),
      sortingField: "eventsourcekey",
    },
    {
      id: "pk",
      header: "DLQ Table PK",
      cell: (item: DLQItem) => item.pk,
      sortingField: "pk",
    },
    {
      id: "timestamp",
      header: "Timestamp",
      cell: (item: DLQItem) => item.timestamp,
      sortingField: "timestamp",
    },
    {
      id: "redrive_attempt_id",
      header: "Latest redrive attempt",
      // Same inline-cell pattern as the other JSX columns above (lint: unstable nested component).
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (item: DLQItem) => <RedriveAttemptCell item={item} />,
      sortingField: "redrive_attempted",
    },
    {
      id: "actions",
      header: "Actions",
      cell: (item: DLQItem) => {
        const logGroup = getDlqLogGroup(item.eventsourcekey, stage, devUser);
        const [startTime, endTime] = getDlqDateRange(item.timestamp);

        return (
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => onItemSelect(item)}>
              {item.is_malformed_json ? "View Message" : "View JSON"}
            </Button>
            <Button onClick={() => onErrorSelect(item)}>View error</Button>
            <div className="divider" />
            <EventButtons
              identifier={item.message_id || item.pk}
              stage={stage}
              logGroups={[logGroup]}
              dateRange={[startTime, endTime]}
              isDlq
            />
            <div className="divider" />
            <DlqRedriveStatusIndicator
              dlqItem={item}
              redriveResult={redrivenMessages.get(item.pk)}
              onRequeue={() => handleRequeueSingle(item.pk)}
            />
          </SpaceBetween>
        );
      },
    },
  ];

  return (
    <Table
      columnDefinitions={columnDefinitions}
      items={items}
      sortingColumn={sortingColumn}
      sortingDescending={sortingDescending}
      onSortingChange={onSortingChange}
      filter={
        <SpaceBetween direction="horizontal" size="s">
          <TextFilter
            filteringText={filteringText}
            filteringPlaceholder="Search DLQ items"
            filteringAriaLabel="Filter DLQ items"
            onChange={({ detail }) => onFilteringChange(detail.filteringText)}
          />
          <Select
            ariaLabel={`Filter by ${RETURNED_AFTER_REDRIVE_LABEL.toLowerCase()}`}
            selectedOption={
              REDRIVE_FILTER_OPTIONS.find((o) => o.value === redriveFilter) ??
              REDRIVE_FILTER_OPTIONS[0]
            }
            options={REDRIVE_FILTER_OPTIONS}
            onChange={({ detail }) =>
              onRedriveFilterChange?.(
                detail.selectedOption.value as RedriveLineageFilter
              )
            }
          />
        </SpaceBetween>
      }
      header={
        <Header
          counter={`(${filteredItemsCount})`}
          description={
            investigateHref
              ? "Investigate opens DLQ Investigations on this transaction: see which case already covers these items, or start one for the whole error population."
              : undefined
          }
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={handleRequeueAll}>Requeue All</Button>
              {investigateHref && (
                <Button
                  href={investigateHref}
                  target="_blank"
                  iconAlign="right"
                  iconName="external"
                  variant="primary"
                >
                  Investigate
                </Button>
              )}
            </SpaceBetween>
          }
        >
          DLQ Items
        </Header>
      }
      pagination={
        <Pagination
          currentPageIndex={currentPageIndex}
          onChange={({ detail }) => onPageChange(detail.currentPageIndex)}
          pagesCount={Math.ceil(filteredItemsCount / pageSize)}
        />
      }
    />
  );
}
